import type { ContentKind } from '@viva/core';
import { useEffect, useRef, useState } from 'react';
import { ErrorMessage } from './components/ErrorMessage';
import { PlayerOverlay } from './components/player/PlayerOverlay';
import { SyncStatus } from './components/SyncStatus';
import { AccountScreen } from './screens/AccountScreen';
import { BrowseScreen } from './screens/BrowseScreen';
import { SearchScreen } from './screens/SearchScreen';
import { SeriesScreen } from './screens/SeriesScreen';
import { HomeScreen } from './screens/HomeScreen';
import { useCatalog } from './state/catalog-context';
import type { CatalogItem } from '@viva/core';
import type { PlaybackRequest } from './state/playback-context';
import { PlaybackContext } from './state/playback-context';
import { t, useLanguage } from './i18n';
import type { MessageKey } from './i18n/en';
import { onBack, restoreFocus } from './spatial-navigation';
import { Icon } from './components/Icon';

type Screen = 'home' | ContentKind | 'search' | 'account';

/** Stroke icons (24×24 path data) shown beside labels, and above them in the phone tab bar. */
const NAV: { id: Screen; label: MessageKey; icon: string }[] = [
  { id: 'home', label: 'nav.home', icon: 'M3 11l9-7 9 7M5 9.5V20h5v-6h4v6h5V9.5' },
  { id: 'live', label: 'nav.live', icon: 'M3 7h18v12H3zM8 3l4 4 4-4' },
  { id: 'movie', label: 'nav.movies', icon: 'M4 4h16v16H4zM8 4v16M16 4v16M4 9h4M4 15h4M16 9h4M16 15h4' },
  { id: 'series', label: 'nav.series', icon: 'M3 8h14v12H3zM7 4h14v12' },
  { id: 'search', label: 'nav.search', icon: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4' },
  { id: 'account', label: 'nav.account', icon: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5' },
];

const SOURCE_KEY = 'viva.browseSource';

/** Remembered source choice; storage can be unavailable (private mode, TV quirks). */
function readSavedSourceId(): string | null {
  try {
    return localStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
}

function saveSourceId(id: string): void {
  try {
    localStorage.setItem(SOURCE_KEY, id);
  } catch {
    // Not remembering the choice only costs convenience.
  }
}

export function App() {
  useLanguage();
  const { task, error, dismissError } = useCatalog();
  const [screen, setScreen] = useState<Screen | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(readSavedSourceId);
  const [playback, setPlayback] = useState<PlaybackRequest | null>(null);
  const [series, setSeries] = useState<CatalogItem | null>(null);
  // Series without a stream URL (Xtream) open their episode list instead of playing.
  /** What opened the player: the remote's highlight goes back to it when the player closes. */
  const opener = useRef<Element | null>(null);
  /** The poster that opened a series: Back from its episodes returns there. */
  const seriesOpener = useRef<Element | null>(null);
  const open = (request: PlaybackRequest) => {
    if (request.item.url === null && request.item.kind === 'series') {
      seriesOpener.current = document.activeElement;
      setSeries(request.item);
    } else {
      opener.current = document.activeElement;
      setPlayback(request);
    }
  };
  // Opening the app shows Home: today's matches first, Live TV and the rest one tap away.
  const current: Screen = screen ?? 'home';
  // A new screen (or a series' episodes): the remote's highlight lands on it, not nowhere.
  // Back from a series returns it to the poster that opened the series.
  const shownScreen = useRef(current);
  useEffect(() => {
    const back = !series && shownScreen.current === current ? seriesOpener.current : null;
    shownScreen.current = current;
    if (!series) seriesOpener.current = null;
    restoreFocus(back);
  }, [current, series]);
  // Back on the remote: out of a series' episodes, else back to Home (the player handles its own Back).
  useEffect(
    () =>
      onBack(() => {
        if (series) {
          setSeries(null);
          return true;
        }
        if (current !== 'home') {
          setScreen('home');
          return true;
        }
        return false;
      }),
    [series, current],
  );

  // Episodes played from an open series remember it, for "Continue watching".
  const openWithSeries = (request: PlaybackRequest) => open(series && !request.series && request.queue ? { ...request, series } : request);

  return (
    <PlaybackContext.Provider value={openWithSeries}>
    <div className="app">
      <nav className="nav" aria-label={t('nav.main')} data-nav-group>
        <div className="nav__brand">
          <img className="logo-mark" src={`${import.meta.env.BASE_URL}logo-mark.png`} alt="" aria-hidden="true" />
          <span>
            IPT<span className="nav__brand-accent">Viva</span>
          </span>
        </div>
        {NAV.map((item) => (
          <button key={item.id} type="button" className={`nav__item nav__item--${item.id}`} aria-current={current === item.id} onClick={() => {
              setSeries(null);
              setScreen(item.id);
            }}>
            <svg className="nav__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d={item.icon} />
            </svg>
            {t(item.label)}
          </button>
        ))}
      </nav>
      {/* Phones: brand and Account at the top, so the tab bar keeps five items. */}
      <header className="topbar">
        <div className="nav__brand">
          <img className="logo-mark" src={`${import.meta.env.BASE_URL}logo-mark.png`} alt="" aria-hidden="true" />
          <span>
            IPT<span className="nav__brand-accent">Viva</span>
          </span>
        </div>
        <button
          type="button"
          className="topbar__account"
          aria-label={t('nav.account')}
          aria-current={current === 'account'}
          onClick={() => {
            setSeries(null);
            setScreen('account');
          }}
        >
          <Icon name="account" />
        </button>
      </header>
      <main className="main">
        {task ? <SyncStatus task={task} /> : null}
        {error ? <ErrorMessage error={error} onDismiss={dismissError} /> : null}
        {series ? <SeriesScreen series={series} onBack={() => setSeries(null)} /> : null}
        {!series && current === 'account' ? <AccountScreen /> : null}
        {!series && current === 'home' ? (
          <HomeScreen
            onSearch={() => setScreen('search')}
          />
        ) : null}
        {/* Search stays mounted under an open series so its results survive "Back". */}
        {current === 'search' ? (
          <div hidden={!!series} className="screen-host">
            <SearchScreen />
          </div>
        ) : null}
        {/* Kept under an open series too, so Back finds the same category, scroll and poster.
            Covered, not display:none: a grid with no size would drop its rows, poster included. */}
        {current === 'live' || current === 'movie' || current === 'series' ? (
          <div className={series ? 'screen-host screen-host--covered' : 'screen-host'} aria-hidden={series ? true : undefined}>
            <BrowseScreen
              kind={current}
              sourceId={sourceId}
              onSourceChange={(id) => {
                setSourceId(id);
                saveSourceId(id);
              }}
            />
          </div>
        ) : null}
      </main>
      {playback ? <PlayerOverlay
          request={playback}
          onClose={() => {
            setPlayback(null);
            restoreFocus(opener.current);
          }}
        /> : null}
    </div>
    </PlaybackContext.Provider>
  );
}
